import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../utils/api';
import InquiriesBlock from '../components/InquiriesBlock';
import { CASE_STAGES, CASE_CATEGORY_MAP, CASE_RESULT_MAP, Chip } from '../utils/cases';

const money = n => Number(n || 0).toLocaleString('ru-RU') + ' ₽';
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

export default function CasesAnalyticsPage() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/cases/stats/summary').then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="empty-state">Загрузка…</div>;

  const stageCnt = Object.fromEntries(s.byStage.map(x => [x.stage, x.cnt]));
  const total = s.byStage.reduce((a, x) => a + x.cnt, 0);
  const active = ['contract', 'pretrial', 'court', 'appeal', 'enforcement'].reduce((a, k) => a + (stageCnt[k] || 0), 0);
  const consult = stageCnt.consultation || 0;
  const refused = stageCnt.refused || 0;
  const conversion = total ? Math.round(((total - consult - refused) / Math.max(total - consult, 1)) * 100) : 0;
  const monthly = s.monthly.map(m => ({ name: MONTHS[Number(m.month.slice(5)) - 1] + ' ' + m.month.slice(2, 4), amount: Number(m.amount) }));

  return (
    <div>
      <div className="page-header"><h1 className="page-title">Аналитика гражданских дел</h1></div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
        <div className="metric-card"><div className="metric-value">{total}</div><div className="metric-label">Всего обращений</div></div>
        <div className="metric-card"><div className="metric-value">{active}</div><div className="metric-label">Дел в работе</div></div>
        <div className="metric-card"><div className="metric-value">{conversion}%</div><div className="metric-label">Конверсия в договор</div><div className="metric-sub">без учёта текущих консультаций</div></div>
        <div className="metric-card"><div className="metric-value">{money(s.money.paid_total)}</div><div className="metric-label">Получено всего</div></div>
        <div className="metric-card"><div className="metric-value">{money(s.money.unpaid_total)}</div><div className="metric-label">Ожидается по графикам</div><div className="metric-sub" style={{ color: Number(s.money.overdue_total) ? 'var(--red)' : undefined }}>просрочено {money(s.money.overdue_total)}</div></div>
      </div>

      <InquiriesBlock />

      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="section-title" style={{ marginBottom: 12 }}>Воронка по стадиям</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {CASE_STAGES.map(st => (
            <div key={st.key} style={{ flex: '1 1 120px', padding: 12, borderRadius: 8, background: st.color + '14', borderTop: `3px solid ${st.color}` }}>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{stageCnt[st.key] || 0}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{st.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 20, marginBottom: 20 }}>
        <div className="card" style={{ padding: 20 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>По категориям (без отказов)</div>
          {!s.byCategory.length && <div className="empty-state">Нет данных</div>}
          {s.byCategory.map(x => {
            const cat = CASE_CATEGORY_MAP[x.category] || CASE_CATEGORY_MAP.other;
            return (
              <div key={x.category} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--border)', gap: 8 }}>
                <Chip color={cat.color}>{cat.label}</Chip>
                <span style={{ fontSize: 13 }}><b>{x.cnt}</b> · {money(x.fee)}</span>
              </div>
            );
          })}
        </div>
        <div className="card" style={{ padding: 20 }}>
          <div className="section-title" style={{ marginBottom: 12 }}>Откуда приходят клиенты</div>
          {!s.bySource.length && <div className="empty-state">Нет данных</div>}
          {s.bySource.map(x => (
            <div key={x.source} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
              <span style={{ fontSize: 13 }}>{x.source}</span><span style={{ fontSize: 13 }}><b>{x.cnt}</b> · договоров {x.contracts}</span>
            </div>
          ))}
          {s.byResult.length > 0 && <>
            <div className="section-title" style={{ margin: '20px 0 12px' }}>Результаты завершённых дел</div>
            {s.byResult.map(x => {
              const r = CASE_RESULT_MAP[x.result];
              return <div key={x.result} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                {r ? <Chip color={r.color}>{r.label}</Chip> : <span>{x.result}</span>}<b>{x.cnt}</b></div>;
            })}
          </>}
        </div>
      </div>

      <div className="card" style={{ padding: 20 }}>
        <div className="section-title" style={{ marginBottom: 12 }}>Поступления по гражданским делам за 12 месяцев</div>
        {!monthly.length ? <div className="empty-state">Оплат пока нет</div> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={12} />
              <YAxis fontSize={12} tickFormatter={v => (v >= 1000 ? Math.round(v / 1000) + 'к' : v)} />
              <Tooltip formatter={v => money(v)} />
              <Bar dataKey="amount" name="Поступления" fill="#4f6ef7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
